import { useState, useEffect } from "react";
import { X } from "lucide-react";
import Swal from "sweetalert2";
import { Item } from "../../../../types/workpermit";

interface MaterialModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  material: Item;
  onSave: (material: Item) => void;
}

export default function MaterialModal({
  isOpen,
  onClose,
  mode,
  material,
  onSave,
}: MaterialModalProps) {
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState(0);
  const [unit, setUnit] = useState("");

  useEffect(() => {
    if (isOpen && material) {
      setDescription(material.Description || "");
      setQuantity(material.ItemQuantity || 0);
      setUnit(material.ItemUnit || "");
    }
  }, [isOpen, material]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (!description.trim()) {
      Swal.fire({ title: "Validation error", text: "Nama material harus diisi", icon: "error", confirmButtonColor: "#2563eb" });
      return;
    }
    if (quantity <= 0) {
      Swal.fire({ title: "Validation error", text: "Jumlah material harus lebih dari 0", icon: "error", confirmButtonColor: "#2563eb" });
      return;
    }
    if (!unit.trim()) {
      Swal.fire({ title: "Validation error", text: "Satuan material harus diisi", icon: "error", confirmButtonColor: "#2563eb" });
      return;
    }

    onSave({
      Description: description.trim(),
      ItemQuantity: Number(quantity),
      ItemUnit: unit.trim(),
    });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4 z-50">
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-sm transition-all duration-300"
        onClick={onClose}
      />
      <div className="relative bg-white dark:bg-gray-800 rounded-3xl w-full max-w-md shadow-2xl z-10 border border-gray-100 dark:border-gray-700 overflow-hidden transform transition-all duration-300">
        <div className="relative px-6 py-5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-xl font-bold text-slate-800 dark:text-white">
            {mode === "add" ? "Add Material" : "Edit Material"}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 rounded-full hover:bg-slate-50 dark:hover:bg-gray-750 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wide">
              Material
            </label>
            <div className="relative">
              <div className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full pl-11 pr-4 py-3 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 transition-all font-semibold text-slate-800 dark:text-slate-100"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wide">
              Quantity
            </label>
            <div className="relative">
              <div className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <input
                type="number"
                value={quantity === 0 ? "" : quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                className="w-full pl-11 pr-4 py-3 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 transition-all font-semibold text-slate-800 dark:text-slate-100"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wide">
              Unit
            </label>
            <div className="relative">
              <div className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10M21 16V10a2 2 0 00-2-2h-3V6" />
                </svg>
              </div>
              <input
                type="text"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                className="w-full pl-11 pr-4 py-3 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 transition-all font-semibold text-slate-800 dark:text-slate-100"
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-5 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            className="px-8 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all text-sm font-semibold shadow-sm active:scale-98"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}
